import { createClient } from '@supabase/supabase-js';
import { createContext, useContext, useState, useEffect, useRef, useMemo } from 'react';

// Initialize Supabase client with explicit persistence settings
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

// Create a single instance of the supabase client with enhanced persistence options
export const supabase = createClient(supabaseUrl, supabaseKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: false, // Disabled to prevent focus-related reloads
    detectSessionInUrl: true,
    storage: localStorage
  }
});

// Create context
const SupabaseContext = createContext(null);

// Force session recovery on page load
if (typeof window !== 'undefined') {
  // This ensures localStorage is synced before any operations
  const savedSession = localStorage.getItem('supabase.auth.token');
  if (savedSession) {
    // console.log('Found saved session in localStorage');
  }
}

// Helper function to call audit endpoints ONLY when explicitly called
const callAuditEndpoint = async (endpoint, userData) => {
  try {
    const apiBaseUrl = process.env.REACT_APP_API_URL || 'https://api.getauric.dev';
    const token = localStorage.getItem('sb-access-token');
    
    if (!token) {
      console.warn('No access token available for audit logging');
      return;
    }

    // console.log(`Explicitly calling ${endpoint} audit endpoint`);
    
    const response = await fetch(`${apiBaseUrl}/api/${endpoint}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(userData)
    });

    if (response.ok) {
      // console.log(`${endpoint} audit log created successfully`);
    } else {
      console.error(`Failed to create ${endpoint} audit log:`, response.status);
    }
  } catch (error) {
    console.error(`Error calling ${endpoint} audit endpoint:`, error);
  }
};

export const SupabaseProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mfaChallengePending, setMfaChallengePending] = useState(false);
  const [authProcessing, setAuthProcessing] = useState(false);
  
  // Track if we've already attempted address creation for this session
  // Using ref to persist across StrictMode re-renders
  const addressCreationAttempted = useRef(new Set());

  // Enhanced session recovery
  useEffect(() => {
    // console.log('SupabaseProvider mounted - initializing auth state');

    // Force immediate session recovery
    const recoverSession = async () => {
      try {
        // console.log('Attempting to recover session...');

        // First try to get session from Supabase
        const { data, error } = await supabase.auth.getSession();

        if (error) {
          console.error('Error recovering session:', error);
          throw error;
        }

        if (data?.session) {
          // console.log('Session recovered successfully:', data.session.user.email);
          setSession(data.session);
          setUser(data.session.user);

          // Explicitly store in localStorage as backup
          try {
            localStorage.setItem('sb-user-id', data.session.user.id);
            localStorage.setItem('sb-access-token', data.session.access_token);
            localStorage.setItem('sb-refresh-token', data.session.refresh_token);
            // console.log('Session data explicitly stored in localStorage');
          } catch (storageError) {
            console.error('Error storing session in localStorage:', storageError);
          }
        } else {
          // console.log('No session found during recovery');

          // Try to recover from our explicit localStorage backup
          const userId = localStorage.getItem('sb-user-id');
          const accessToken = localStorage.getItem('sb-access-token');
          const refreshToken = localStorage.getItem('sb-refresh-token');

          if (userId && accessToken) {
            // console.log('Found backup session data in localStorage, attempting to restore');
            try {
              const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession({
                refresh_token: refreshToken,
              });

              if (refreshError) {
                console.error('Error refreshing session from backup:', refreshError);
              } else if (refreshData?.session) {
                // console.log('Session restored from backup');
                setSession(refreshData.session);
                setUser(refreshData.session.user);
              }
            } catch (refreshError) {
              console.error('Exception during session refresh:', refreshError);
            }
          }
        }
      } catch (e) {
        console.error('Unexpected error during session recovery:', e);
      } finally {
        setLoading(false);
      }
    };

    // Run session recovery immediately
    recoverSession();

    // Set up auth state change listener - NO AUTOMATIC AUDIT LOGGING
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, newSession) => {
        // console.log('Auth state changed:', event);

        if (newSession) {
          // console.log('New session established for:', newSession.user.email);
          setSession(newSession);
          setUser(newSession.user);

          // Explicitly store in localStorage as backup
          try {
            localStorage.setItem('sb-user-id', newSession.user.id);
            localStorage.setItem('sb-access-token', newSession.access_token);
            localStorage.setItem('sb-refresh-token', newSession.refresh_token);
            // console.log('Session data updated in localStorage');
          } catch (storageError) {
            console.error('Error storing session in localStorage:', storageError);
          }

          // Check if user needs a default address created from signup metadata
          // This handles both manual login and automatic session restoration
          // Only attempt once per session to prevent duplicates
          const userId = newSession.user.id;
          if (!addressCreationAttempted.current.has(userId)) {
            try {
              const userMetadata = newSession.user.user_metadata || {};

              // If user has name or address data in metadata, sync with backend
              if (userMetadata.first_name || userMetadata.last_name || userMetadata.address_line_1) {
                // Mark as attempted to prevent duplicates (using Set for StrictMode safety)
                addressCreationAttempted.current.add(userId);
                
                // First, sync the user with backend to ensure they exist in the database
                const apiBaseUrl = process.env.REACT_APP_API_URL || 'https://api.getauric.dev';
                const syncResponse = await fetch(`${apiBaseUrl}/api/users/sync`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${newSession.access_token}`
                  },
                  body: JSON.stringify({
                    user_metadata: userMetadata
                  })
                });

                if (syncResponse.ok) {
                  
                  // Now check if user has any existing addresses
                  const addressResponse = await fetch(`${apiBaseUrl}/api/account/addresses`, {
                    method: 'GET',
                    headers: {
                      'Authorization': `Bearer ${newSession.access_token}`
                    }
                  });

                  if (addressResponse.ok) {
                    const addressData = await addressResponse.json();
                    const existingAddresses = addressData.addresses || [];
                    
                    if (existingAddresses.length === 0) {
                      
                      // Create default address from metadata (no phone - stored in user table)
                      const newAddressData = {
                        first_name: userMetadata.first_name,
                        last_name: userMetadata.last_name,
                        address_line_1: userMetadata.address_line_1,
                        address_line_2: userMetadata.address_line_2,
                        city: userMetadata.city,
                        state: userMetadata.state,
                        zip_code: userMetadata.zip_code,
                        country: userMetadata.country || 'United States',
                        is_default: true
                      };

                      const createResponse = await fetch(`${apiBaseUrl}/api/account/addresses`, {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                          'Authorization': `Bearer ${newSession.access_token}`
                        },
                        body: JSON.stringify(newAddressData)
                      });

                      if (!createResponse.ok) {
                        console.error(`Failed to create default address for ${newSession.user.email}:`, createResponse.status);
                        addressCreationAttempted.current.delete(userId); // Remove from Set on failure so it can be retried
                      }
                    }
                  } else {
                    console.error('Failed to fetch existing addresses:', addressResponse.status);
                    addressCreationAttempted.current.delete(userId); // Remove from Set on failure so it can be retried
                  }
                } else {
                  console.error('Failed to sync user with backend:', syncResponse.status);
                  addressCreationAttempted.current.delete(userId); // Remove from Set on failure so it can be retried
                }
              } else {
                // console.log('No address metadata found for user');
              }
            } catch (addressError) {
              console.error('Error handling address creation on auth state change:', addressError);
              addressCreationAttempted.current.delete(userId); // Remove from Set on error so it can be retried
              // Don't fail auth if address creation fails
            }
          }

          // NO AUTOMATIC AUDIT LOGGING HERE
          // Audit logging is handled explicitly in login/logout functions
        } else if (event === 'SIGNED_OUT') {
          // console.log('User signed out');
          setSession(null);
          setUser(null);
          addressCreationAttempted.current.clear(); // Clear all address creation tracking on logout

          // Clear backup storage
          localStorage.removeItem('sb-user-id');
          localStorage.removeItem('sb-access-token');
          localStorage.removeItem('sb-refresh-token');
          
          // Don't clear MFA challenge pending immediately on sign out, as this might be part of MFA flow
        }

        setLoading(false);
      }
    );

    // Cleanup function
    return () => {
      // console.log('SupabaseProvider unmounting - cleaning up');
      if (subscription) {
        // console.log('Unsubscribing from auth state changes');
        subscription.unsubscribe();
      }
    };
  }, []);

  // Login with email and password
  const login = async (email, password) => {
    try {
      setLoading(true);
      setAuthProcessing(true);
      setError(null);

      // console.log('Attempting login for:', email);
      
      // First, check if user has MFA enabled without actually signing in
      try {
        // Try a quick sign in to check MFA status
        const { data: tempData, error: tempError } = await supabase.auth.signInWithPassword({
          email,
          password,
        });

        if (tempError) {
          throw tempError;
        }

        // console.log('Temporary login successful, checking MFA status');

        // Check if user has MFA factors
        const { data: factors, error: factorError } = await supabase.auth.mfa.listFactors();
        // console.log('MFA factors check:', { factors, factorError });
        
        if (!factorError && factors?.totp && factors.totp.length > 0) {
          // console.log('User has MFA enabled with factors:', factors.totp);
          // console.log('Checking AAL (Authenticator Assurance Level):', tempData.session?.aal);
          
          // Check if we have the required authentication level
          if (tempData.session?.aal !== 'aal2') {
            // console.log('User needs to complete MFA challenge - signing out immediately');
            setMfaChallengePending(true);
            
            // Sign out immediately before any state updates can trigger navigation
            await supabase.auth.signOut();
            
            // Store MFA state in sessionStorage for the login page to pick up
            sessionStorage.setItem('mfa_required', JSON.stringify({
              factorId: factors.totp[0].id,
              email: email,
              password: password
            }));
            
            throw { 
              message: 'Two-factor authentication required', 
              requiresMFA: true, 
              factorId: factors.totp[0].id,
              email: email,
              password: password
            };
          } else {
            // console.log('User already completed MFA challenge (AAL2)');
          }
        } else {
          // console.log('User does not have MFA enabled');
        }

        // console.log('Login successful - calling audit endpoint');

        // EXPLICITLY call login audit endpoint after successful login
        await callAuditEndpoint('login', {
          email: email,
          user_id: tempData.user.id
        });

        return tempData;
      } catch (mfaCheckError) {
        // If this is an MFA requirement, rethrow it
        if (mfaCheckError.requiresMFA) {
          throw mfaCheckError;
        }
        // Otherwise, it's a regular auth error
        throw mfaCheckError;
      }
    } catch (error) {
      console.error('Login error:', error);
      setError(error.message);
      // Don't clear MFA challenge pending if this is an MFA requirement error
      if (!error.requiresMFA) {
        setMfaChallengePending(false);
      }
      throw error;
    } finally {
      setLoading(false);
      setAuthProcessing(false);
    }
  };

  // Complete login with MFA
  const loginWithMFA = async (email, password, factorId, code) => {
    try {
      setLoading(true);
      setError(null);

      // console.log('Attempting login with MFA for:', email);
      
      // First, sign in with password
      const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (signInError) {
        throw signInError;
      }

      // Then verify MFA
      const { data: mfaData, error: mfaError } = await supabase.auth.mfa.challengeAndVerify({
        factorId,
        code
      });

      if (mfaError) {
        // Sign out on MFA failure
        await supabase.auth.signOut();
        throw mfaError;
      }

      // console.log('MFA verification successful');
      
      // Clear MFA challenge pending state
      setMfaChallengePending(false);
      
      // Call audit endpoint after successful MFA login
      await callAuditEndpoint('login', {
        email: email,
        user_id: signInData.user.id
      });

      return mfaData;
    } catch (error) {
      console.error('MFA login error:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Verify MFA challenge during login
  const verifyMFA = async (factorId, code) => {
    try {
      setLoading(true);
      setError(null);

      const { data, error } = await supabase.auth.mfa.challengeAndVerify({
        factorId,
        code
      });

      if (error) {
        throw error;
      }

      // console.log('MFA verification successful');
      return data;
    } catch (error) {
      console.error('MFA verification error:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Sign up with email and password
  const signup = async (email, password, userMetadata = {}) => {
    try {
      setLoading(true);
      setError(null);

      // console.log('Attempting signup for:', email);

      // First, check if email already exists via backend API
      const apiBaseUrl = process.env.REACT_APP_API_URL || 'https://api.getauric.dev';
      try {
        const checkResponse = await fetch(`${apiBaseUrl}/auth/check-email`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ email: email.toLowerCase().trim() })
        });

        if (checkResponse.ok) {
          const checkData = await checkResponse.json();
          if (checkData.exists) {
            throw new Error('An account with this email already exists');
          }
        } else {
          console.warn('Email check endpoint returned non-OK status:', checkResponse.status);
        }
      } catch (checkError) {
        // If it's our "already exists" error, rethrow it
        if (checkError.message === 'An account with this email already exists') {
          throw checkError;
        }
        // Otherwise log and continue - we'll let Supabase handle duplicate check as fallback
        console.warn('Email check failed, continuing with signup:', checkError);
      }

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: userMetadata  // Store metadata for later use during first login
        }
      });

      if (error) {
        // Check for Supabase duplicate user error messages
        const errorMsg = error.message?.toLowerCase() || '';
        if (errorMsg.includes('already registered') || errorMsg.includes('already exists') || errorMsg.includes('user already')) {
          throw new Error('An account with this email already exists');
        }
        throw error;
      }

      // Check if Supabase returned a user but it's a duplicate (identities array is empty)
      if (data?.user && data.user.identities && data.user.identities.length === 0) {
        throw new Error('An account with this email already exists');
      }

      // console.log('Signup successful - address will be created on first login');

      return data;
    } catch (error) {
      console.error('Signup error:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Login with social provider
  const loginWithSocial = async (provider) => {
    try {
      setLoading(true);
      setError(null);

      // console.log(`Attempting ${provider} login`);
      
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider,
      });

      if (error) {
        throw error;
      }

      // console.log(`${provider} login initiated`);
      
      // Note: For OAuth, the actual login completion happens in the callback
      // You might need to call the audit endpoint in the OAuth callback handler
      
      return data;
    } catch (error) {
      console.error(`Social login error (${provider}):`, error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Logout
  const logout = async () => {
    try {
      setLoading(true);
      setError(null);

      // console.log('Logging out user');

      // EXPLICITLY call logout audit endpoint BEFORE clearing session
      if (user) {
        // console.log('Calling logout audit endpoint');
        try {
          await callAuditEndpoint('logout', {
            email: user.email,
            user_id: user.id
          });
        } catch (auditError) {
          // Don't fail logout if audit fails
          console.warn('Audit endpoint failed during logout:', auditError);
        }
      }

      // Clear any MFA state
      setMfaChallengePending(false);
      setAuthProcessing(false);
      sessionStorage.removeItem('mfa_required');

      // Try to sign out from Supabase, but don't fail if session is already gone
      try {
        await supabase.auth.signOut({ scope: 'local' });
      } catch (signOutError) {
        // Session might already be expired/missing - that's fine, we're logging out anyway
        console.warn('Supabase signOut error (session may already be expired):', signOutError.message);
      }

      // Clear ALL Supabase-related storage (covers all possible key formats)
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.startsWith('sb-') || key.includes('supabase'))) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));

      // Also clear our custom backup keys
      localStorage.removeItem('sb-user-id');
      localStorage.removeItem('sb-access-token');
      localStorage.removeItem('sb-refresh-token');

      // console.log('Logout successful');
      setUser(null);
      setSession(null);
    } catch (error) {
      console.error('Logout error:', error);
      setError(error.message);
      // Still clear state even on error
      setUser(null);
      setSession(null);
    } finally {
      setLoading(false);
    }
  };

  // Reset password
  const resetPassword = async (email) => {
    try {
      setLoading(true);
      setError(null);

      // console.log('Requesting password reset for:', email);
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) {
        throw error;
      }

      // console.log('Password reset email sent');
      return true;
    } catch (error) {
      console.error('Reset password error:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Update password
  const updatePassword = async (newPassword) => {
    try {
      setLoading(true);
      setError(null);

      // console.log('Updating password');
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) {
        throw error;
      }

      // console.log('Password updated successfully');
      return true;
    } catch (error) {
      console.error('Update password error:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Get access token for API calls with enhanced fallback mechanisms
  const getAccessToken = async () => {
    // First try to get from session state
    if (session?.access_token) {
      return session.access_token;
    }

    // If not in session state, try our explicit localStorage backup
    const accessToken = localStorage.getItem('sb-access-token');
    if (accessToken) {
      // console.log('Using access token from localStorage backup');
      return accessToken;
    }

    // If still no token, try to refresh the session
    try {
      // console.log('Refreshing session to get access token');
      const { data, error } = await supabase.auth.getSession();
      if (error) {
        throw error;
      }

      if (data?.session?.access_token) {
        // Update state
        setSession(data.session);
        setUser(data.session.user);
        // console.log('Session refreshed successfully');

        // Update backup
        localStorage.setItem('sb-user-id', data.session.user.id);
        localStorage.setItem('sb-access-token', data.session.access_token);
        localStorage.setItem('sb-refresh-token', data.session.refresh_token);

        return data.session.access_token;
      }
    } catch (error) {
      console.error('Error refreshing token:', error);
    }

    // console.log('No access token available');
    return null;
  };

  // Check if user is authenticated with enhanced checks
  // Memoize to prevent unnecessary re-renders of consuming components
  const isAuthenticated = useMemo(() => {
    // Don't consider user authenticated if MFA challenge is pending or auth is processing
    if (mfaChallengePending || authProcessing) {
      return false;
    }

    // First check React state
    if (user) {
      return true;
    }

    // Then check localStorage backup as fallback
    const userId = localStorage.getItem('sb-user-id');
    const accessToken = localStorage.getItem('sb-access-token');

    return !!(userId && accessToken);
  }, [user, mfaChallengePending, authProcessing]);

  const value = useMemo(() => ({
    user,
    session,
    loading,
    error,
    authProcessing,
    isAuthenticated,
    login,
    signup,
    loginWithSocial,
    logout,
    resetPassword,
    updatePassword,
    getAccessToken,
    verifyMFA,
    loginWithMFA,
    supabase, // Expose supabase client for direct access if needed
  }), [user, session, loading, error, authProcessing, isAuthenticated, login, signup, loginWithSocial, logout, resetPassword, updatePassword, getAccessToken, verifyMFA, loginWithMFA]);

  return (
    <SupabaseContext.Provider value={value}>
      {children}
    </SupabaseContext.Provider>
  );
};

// Custom hook to use the Supabase context
export const useSupabase = () => {
  const context = useContext(SupabaseContext);
  if (!context) {
    throw new Error('useSupabase must be used within a SupabaseProvider');
  }
  return context;
};

